// 📁 src/components/Payment/BkashForm.jsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fakeBkashOtpAPI, fakeBkashPaymentAPI } from '../../utils/paymentMocks';
import { recordPayment } from '../../utils/recordPayment';
import '../../styles/payment.css';
import bkashLogo from '../../images/bkash-logo.png';
import Toast from '../common/Toast';
import '../common/Toast.css';

const BkashForm = ({
  selectedModules,
  onSuccess,
  onFailure,
  companyName,
  tenantId,
  billingCycle,
  currency,
  country, // ✅ added for visibility check
  annualDiscountRate = 0 // ✅ Canonical discount prop
}) => {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [errorToast, setErrorToast] = useState('');
  const [emailStatusMessage, setEmailStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false); // ✅ track paid state
  const navigate = useNavigate();

  const grandTotal = Math.round(selectedModules.reduce((sum, mod) => sum + mod.price, 0));

  const sendOtp = async () => {
    try {
      const response = await fakeBkashOtpAPI(mobile);
      if (response.status === 'sent') {
        setOtpSent(true);
      } else {
        onFailure?.(response.message);
        setErrorToast(response.message);
        setTimeout(() => setErrorToast(''), 4000);
      }
    } catch (error) {
      onFailure?.(error.message);
      setErrorToast(error.message);
      setTimeout(() => setErrorToast(''), 4000);
    }
  };

  const handlePayment = async () => {
    // ✅ enforce BDT-only
    if (currency !== 'BDT') {
      setErrorToast('Only BDT payments are allowed via bKash.');
      setTimeout(() => setErrorToast(''), 4000);
      return;
    }

    setLoading(true);
    try {
      const response = await fakeBkashPaymentAPI({
        mobile,
        otp,
        amount: grandTotal,
        currency
      });
      if (response.status === 'success') {
        const email =
          sessionStorage.getItem('isLoggedIn') === 'true' ? localStorage.getItem('email') : null;

        console.log('🧾 Logging payment with companyName:', companyName);

        const result = await recordPayment({
          email,
          amount: grandTotal,
          method: 'bKash',
          selectedModules,
          companyName,
          tenantId,
          billingCycle,
          currency,
          annualDiscountRate // ✅ Canonical field passed
        });

        const invoiceNo = result?.invoiceNo || 'N/A';
        sessionStorage.setItem('invoicePreviewData', JSON.stringify({ email, invoiceNo }));

        console.log('📤 Triggering invoice email with:', { email });

        try {
          const emailResponse = await fetch('http://localhost:3001/api/email/send-invoice-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
          });

          const result = await emailResponse.json();
          if (emailResponse.ok && result.success) {
            setEmailStatusMessage('📧 A payment confirmation email has been sent to your email.');
          } else {
            setEmailStatusMessage('⚠️ Payment succeeded, but email confirmation failed.');
          }
        } catch (err) {
          setEmailStatusMessage('⚠️ Payment succeeded, but email confirmation failed.');
        }

        onSuccess?.();
        setShowSuccessToast(true);
        setPaid(true); // ✅ mark as paid
        setTimeout(() => setShowSuccessToast(false), 4000);

        setTimeout(() => {
          window.open('/invoice-preview', '_blank');
          navigate('/my-account/software');
        }, 2000);
      } else {
        onFailure?.(response.message);
        setErrorToast(response.message);
        setTimeout(() => setErrorToast(''), 4000);
      }
    } catch (error) {
      onFailure?.(error.message);
      setErrorToast(error.message);
      setTimeout(() => setErrorToast(''), 4000);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Bangladesh-only visibility
  if (country !== 'Bangladesh') {
    return null;
  }

  return (
    <div className="bkash-form">
      <img src={bkashLogo} alt="bKash Logo" className="payment-logo" />
      <h3>Pay with bKash</h3>
      <input
        type="text"
        placeholder="bKash Mobile Number"
        value={mobile}
        onChange={(e) => setMobile(e.target.value)}
      />
      {!otpSent && (
        <button onClick={sendOtp} disabled={loading || paid}>
          {loading ? 'Sending OTP...' : 'Send OTP'}
        </button>
      )}
      {otpSent && (
        <>
          <input
            type="text"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
          <button onClick={handlePayment} disabled={loading || paid}>
            {loading ? (
              <span>
                <span className="spinner" /> Processing payment…
              </span>
            ) : paid ? (
              'Paid'
            ) : (
              `Pay ${currency} ${grandTotal.toFixed(2)}`
            )}
          </button>
        </>
      )}
      {showSuccessToast && (
        <>
          <Toast
            message={`Payment of ${currency} ${grandTotal.toFixed(2)} successful!`}
            type="success"
            onClose={() => setShowSuccessToast(false)}
          />
          {emailStatusMessage && (
            <p style={{ marginTop: '8px', color: 'green', fontWeight: 'bold' }}>
              {emailStatusMessage}
            </p>
          )}
        </>
      )}
      {errorToast && <Toast message={errorToast} type="error" onClose={() => setErrorToast('')} />}
    </div>
  );
};

export default BkashForm;
